/**
 * Appearance resolution & persistence (doc 147 §8.9.6).
 *
 * Atlas's UI exposes a tri-state mode {auto · dark · light}. `auto` follows
 * the operating system's `prefers-color-scheme`; `dark`/`light` are explicit
 * overrides. The user's choice is persisted to Firestore (durable, syncs
 * across devices) and mirrored to localStorage (so the first paint of every
 * subsequent visit lands in the right palette without a network round-trip
 * — see the inline FOUC script in `index.html`).
 *
 * This module is pure and dependency-injected — every external input
 * (storage, system pref) is passed in — so the resolution chain can be tested
 * exhaustively without globals.
 *
 * Resolution chain (each layer optional):
 *    Firestore preferences → localStorage cache → matchMedia → dark
 */
import type { DensityMode, Appearance } from '../theme/cssVariables.js';
import type { GraphContrast } from '../theme/tokens.js';

/** Tri-state mode setting; `auto` defers to the OS preference. */
export type ModeSetting = 'auto' | 'dark' | 'light';

/** What the user picks; resolves to `Appearance` after consulting the system. */
export interface AppearanceSetting {
  mode: ModeSetting;
  density: DensityMode;
  graphContrast: GraphContrast;
}

export const defaultAppearanceSetting: AppearanceSetting = {
  mode: 'auto',
  density: 'compact',
  graphContrast: 'standard',
};

/** Dependency-injected source for the OS `prefers-color-scheme`. */
export interface SystemPrefSource {
  prefersDark: () => boolean;
}

/** localStorage key holding the cached AppearanceSetting JSON. */
export const STORAGE_KEY = 'atlas.appearance';

/* ─── Resolution ──────────────────────────────────────────────────────── */

/**
 * Collapse a `setting` (plus the OS preference and density / contrast) into
 * the concrete `Appearance` consumed by `applyAppearance`. `system === null`
 * is the no-matchMedia fallback (older browsers, SSR) — defaults to dark.
 */
export function resolveAppearance(
  setting: AppearanceSetting,
  system: SystemPrefSource | null,
): Appearance {
  let mode: 'dark' | 'light';
  if (setting.mode === 'dark' || setting.mode === 'light') {
    mode = setting.mode;
  } else if (system) {
    mode = system.prefersDark() ? 'dark' : 'light';
  } else {
    mode = 'dark';
  }
  return { mode, density: setting.density, graphContrast: setting.graphContrast };
}

/* ─── Normalization ───────────────────────────────────────────────────── */

const MODES: readonly ModeSetting[] = ['auto', 'dark', 'light'];
const DENSITIES: readonly DensityMode[] = ['compact', 'regular', 'expanded'];
const CONTRASTS: readonly GraphContrast[] = ['standard', 'high'];

/** Coerce an untrusted value (localStorage, Firestore) into a valid setting. */
export function normalizeAppearanceSetting(raw: unknown): AppearanceSetting {
  if (typeof raw !== 'object' || raw === null) return { ...defaultAppearanceSetting };
  const r = raw as Record<string, unknown>;
  const mode = (MODES as readonly string[]).includes(r.mode as string)
    ? (r.mode as ModeSetting)
    : 'auto';
  const density = (DENSITIES as readonly string[]).includes(r.density as string)
    ? (r.density as DensityMode)
    : 'compact';
  const graphContrast = (CONTRASTS as readonly string[]).includes(r.graphContrast as string)
    ? (r.graphContrast as GraphContrast)
    : 'standard';
  return { mode, density, graphContrast };
}

/* ─── localStorage ────────────────────────────────────────────────────── */

/** Read and normalize the cached AppearanceSetting; defaults on miss/error. */
export function loadCachedAppearanceSetting(storage: Storage): AppearanceSetting {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return { ...defaultAppearanceSetting };
    return normalizeAppearanceSetting(JSON.parse(raw));
  } catch {
    // Safari private mode, quota errors, JSON.parse failures — fall back.
    return { ...defaultAppearanceSetting };
  }
}

/** Persist a setting; silently no-ops if storage is unavailable. */
export function saveCachedAppearanceSetting(storage: Storage, setting: AppearanceSetting): void {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(setting));
  } catch {
    /* swallow — appearance is best-effort. */
  }
}
